import React, { useEffect, useState, useRef } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import ShareOutlined from "@mui/icons-material/ShareOutlined";
import axios from "axios";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { useParams } from "react-router-dom";
import { Payment } from "../../models/payment";
import { Loan } from "../../models/loan";
import { useT } from "../../i18n";
import { formatCurrency } from "../../utils/format/currency";
import { PageHeader } from "@routes-plan/ui";
import { pageSx } from "./components/layout";
import { ScreenTourButton } from "../../components/help/ScreenHelp";

interface ShowAlerts {
  /** Abono a mostrar; si no viene se toma el id de la URL. */
  payId?: number | string;
  showAlert: (message: string, severity?: "success" | "info" | "warning" | "error") => void;
  showLoading: () => void;
  hideLoading: () => void;
}
const PaymentReceipt: React.FC<ShowAlerts> = ({ payId, showAlert, showLoading, hideLoading }) => {
  const t = useT();
  const params = useParams();
  const id = payId ?? params.id;
  const [payment, setPayment] = useState<Payment | null>(null);
  const [loan, setLoan] = useState<Loan | null>(null);
  const invoiceRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const fetchData = async () => {
      showLoading();
      try {
        const { data: paymentData } = await axios.get(`payments/${id}`);
        setPayment(paymentData);
        if (paymentData.loan?.id) {
          const { data: loanData } = await axios.get(`loans/${paymentData.loan.id}`);
          setLoan(loanData);
        }
      } catch (error) {
        showAlert(t('payments.receiptUnavailable'), 'error');
      } finally {
        hideLoading();
      }
    };
    fetchData();
  }, [id, showAlert, showLoading, hideLoading]);

  const payDate = payment?.date || payment?.operationDate || '';
  // `cash` llega como 1/0 o true/false.
  const isCash = Boolean(Number(payment?.cash ?? 0));
  // Quien registró el abono (no quien hizo el préstamo).
  const collectorName =
    [payment?.user?.firstName, payment?.user?.lastName].filter(Boolean).join(' ').trim() || payment?.user?.username || '';
  const formattedDate = payDate.slice(0, 10).replaceAll("-", "");
  const formattedTime = payDate.slice(11, 19).replaceAll(":", "").replace(".", "");
  const tcktNmbr = `${formattedDate}${formattedTime}-${payment?.id}`;
  const hidePdfButtons = () => {
    document.querySelectorAll('.pdf-hide').forEach((element) => {
      (element as HTMLElement).style.display = 'none';
    });
  };

  // Función para mostrar los botones nuevamente
  const showPdfButtons = () => {
    document.querySelectorAll('.pdf-hide').forEach((element) => {
      (element as HTMLElement).style.display = 'inline-block';
    });
  };
  async function handlePrint(clientFullName: string) {
    if (invoiceRef.current && loan?.client?.fullName && payment) {
      const clientName = loan.client.fullName.replace(/\s+/g, '_');
      const fileName = t('payments.receiptFileName', { client: clientName, ticket: tcktNmbr });

      // Oculta los botones antes de generar el PDF
      hidePdfButtons();
      await new Promise((resolve) => setTimeout(resolve, 300));
      // Esperar un momento para asegurar que los elementos se hayan ocultado
      const pdfWidth = 75; // Ancho en mm (7.5 cm)

      // Genera el canvas
      const canvas = await html2canvas(invoiceRef.current!, {
        scale: 4, // Ajusta la escala para mejorar la calidad
        useCORS: true,
        scrollX: 0,
        scrollY: 0,
        width: invoiceRef.current!.scrollWidth,
        height: invoiceRef.current!.scrollHeight,
      });

      // Calcular la altura en función del contenido
      const imgData = canvas.toDataURL('image/png');
      const imgHeight = (canvas.height * pdfWidth) / canvas.width; // Mantener la proporción

      // Crear el PDF con la altura dinámica calculada
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: [pdfWidth, imgHeight], // Ajustar el alto según el contenido
      });

      // Agregar la imagen al PDF
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, imgHeight, undefined, 'FAST');
      pdf.save(fileName);

      // Volver a mostrar los botones después de generar el PDF
      showPdfButtons();
    }
  }

  async function handleSharePDF(clientFullName: string) {
    if (invoiceRef.current && clientFullName) {
      const clientName = clientFullName.replace(/\s+/g, '_');
      const fileName = t('payments.receiptFileName', { client: clientName, ticket: tcktNmbr });

      // Oculta los botones antes de generar el PDF
      hidePdfButtons();
      await new Promise((resolve) => setTimeout(resolve, 300));
      // Esperar un momento para asegurar que los elementos se hayan ocultado
      const pdfWidth = 75; // Ancho en mm (7.5 cm)

      // Genera el canvas
      const canvas = await html2canvas(invoiceRef.current!, {
        scale: 4, // Ajusta la escala para mejorar la calidad
        useCORS: true,
        scrollX: 0,
        scrollY: 0,
        width: invoiceRef.current!.scrollWidth,
        height: invoiceRef.current!.scrollHeight,
      });

      // Calcular la altura en función del contenido
      const imgData = canvas.toDataURL('image/png');
      const imgHeight = (canvas.height * pdfWidth) / canvas.width; // Mantener la proporción

      // Crear el PDF con la altura dinámica calculada
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: [pdfWidth, imgHeight], // Ajustar el alto según el contenido
      });

      // Agregar la imagen al PDF
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, imgHeight, undefined, 'FAST');

      const pdfBlob = pdf.output('blob');

      try {
        if (navigator.canShare && navigator.canShare({ files: [new File([pdfBlob], fileName, { type: "application/pdf" })] })) {
          const file = new File([pdfBlob], fileName, { type: "application/pdf" });
          await navigator.share({
            title: t('payments.shareTitle'),
            text: t('payments.shareText'),
            files: [file],
          });
        } else {
          const pdfUrl = URL.createObjectURL(pdfBlob);
          window.open(pdfUrl, '_blank');
          showAlert(t('payments.shareUnsupported'), "info");
        }
      } catch (error) {
        console.error('Error al generar el PDF o al compartir:', error);
        showAlert(t('payments.shareError'), 'error');
      }

      // Volver a mostrar los botones después de generar el PDF
      showPdfButtons();
    } else {
      showAlert(t('payments.receiptUnavailable'), "warning");
    }
  }


  const line = (label: string, value: React.ReactNode, strong = false) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ textAlign: 'right', minWidth: 0, overflowWrap: 'anywhere', fontWeight: strong ? 600 : 400 }}>
        {value === '' || value == null ? '—' : value}
      </Typography>
    </Box>
  );

  const receipt = (
    <Box sx={{ width: '100%', maxWidth: 400, mx: 'auto' }}>
      {/* Lo que va dentro de invoiceRef es lo que sale en el PDF (75 mm de ancho). */}
      <Paper variant="outlined" ref={invoiceRef} id="Invoice" sx={{ p: 2.5 }} data-tour="payment-receipt-ticket">
        <Typography variant="caption" color="text.secondary" component="div" sx={{ textAlign: 'right' }}>
          {t('payments.receiptNumber', { number: tcktNmbr })}
        </Typography>
        <Typography variant="h6" component="h2" sx={{ textAlign: 'center', my: 1 }}>
          {t('payments.receiptHeading')}
        </Typography>
        <Divider sx={{ mb: 1 }} />
        {line(t('payments.receiptClientLabel'), loan?.client?.fullName)}
        {line(t('payments.receiptIdLabel'), loan?.client?.idNumber)}
        {line(t('payments.receiptAddressLabel'), loan?.client?.mainAddress)}
        {line(t('payments.receiptDateLabel'), payDate ? `${payDate.slice(0, 10)} ${payDate.slice(11, 16)}` : '')}
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
          {t('payments.receiptSummary')}
        </Typography>
        {line(t('payments.receiptLoanTotalLabel'), loan?.totalValue != null ? formatCurrency(loan.totalValue) : '')}
        {/* Forma de pago real del abono: efectivo o transferencia (con su banco y comprobante). */}
        {line(t('payments.paymentMethod'), payment ? (isCash ? t('payments.cash') : t('payments.transfer')) : '')}
        {payment && !isCash && (payment.bank?.name || payment.bankReceipt)
          ? line(t('payments.bank'), t('payments.receiptBankLine', { bank: payment.bank?.name || '—', receipt: payment.bankReceipt || '—' }))
          : null}
        {/* Los abonos no tienen número de cuota: se muestra el valor abonado y, si hubo, el descuento. */}
        {line(t('payments.paidValue'), payment?.value != null ? formatCurrency(payment.value) : '', true)}
        {Number(payment?.discountValue ?? 0) > 0 ? line(t('payments.discountValue'), formatCurrency(payment?.discountValue ?? 0)) : null}
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 2 }}>
          {t('payments.receiptGeneratedBy')} {collectorName || t('payments.unknownUser')}
        </Typography>
      </Paper>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'flex-end', gap: 1, mt: 2 }}>
        <Button className="pdf-hide" variant="outlined" data-tour="payment-receipt-share" startIcon={<ShareOutlined />} onClick={() => handleSharePDF(loan?.client?.fullName || t('payments.clientFallback'))}>
          {t('payments.share')}
        </Button>
        <Button className="pdf-hide" variant="contained" data-tour="payment-receipt-download" startIcon={<FileDownloadOutlined />} onClick={() => handlePrint(loan?.client?.fullName || t('payments.clientFallback'))}>
          {t('payments.download')}
        </Button>
      </Box>
    </Box>
  );

  // Dentro del modal del detalle del préstamo se muestra solo el recibo; como página, con su encabezado.
  if (payId != null) return receipt;
  return (
    <Box sx={pageSx}>
      <PageHeader
        title={t('payments.receiptTitle')}
        breadcrumbs={[{ label: t('menu.loans.payments'), href: '#/loans/payments' }, { label: t('payments.receiptTitle') }]}
        helpAction={<ScreenTourButton />}
      />
      {receipt}
    </Box>
  );
};

export default PaymentReceipt;
